import { Badge } from "shared/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "shared/ui/dialog"
import { Separator } from "shared/ui/separator"
import { Loader2, CheckCircle2, AlertTriangle, GitBranch } from "lucide-react"
import type { WorkflowSimulationResult, WorkflowSimulationStage } from "../api/workflowService"

interface WorkflowSimulationResultDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isLoading: boolean
  error?: string | null
  result?: WorkflowSimulationResult | null
}

const StageItem = ({ stage }: { stage: WorkflowSimulationStage }) => {
  const displayName =
    stage.approverRoleName || stage.approverRole || stage.approverRoleCode || stage.approverRoleId
  const secondaryCode =
    stage.approverRoleCode && stage.approverRoleCode !== stage.approverRoleName
      ? stage.approverRoleCode
      : undefined

  return (
    <div className="relative pl-6">
      <div className="absolute left-0 top-2 h-3 w-3 rounded-full border-2 border-indigo-500 bg-white" />
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
        <div className="flex items-center justify-between text-sm font-medium text-slate-900">
          <span>{stage.name || stage.code || stage.stageId}</span>
          <Badge variant="outline" className="text-[11px]">
            #{stage.sequence}
          </Badge>
        </div>
        <div className="mt-1 space-y-1 text-xs text-slate-500">
          {displayName ? (
            <div>
              <p className="text-slate-600">
                Approver: <span className="font-medium text-slate-900">{displayName}</span>
                {secondaryCode ? <span className="text-slate-500"> ({secondaryCode})</span> : null}
              </p>
              {stage.approverRoleDescription ? (
                <p className="text-[11px] text-slate-500">{stage.approverRoleDescription}</p>
              ) : null}
            </div>
          ) : null}
          <p>Gate: {stage.isGate ? "Yes" : "No"}</p>
        </div>
      </div>
    </div>
  )
}

export function WorkflowSimulationResultDialog({
  open,
  onOpenChange,
  isLoading,
  error,
  result,
}: WorkflowSimulationResultDialogProps) {
  const matchedRule = result?.matchedRule
  const stages = result?.stages ?? []
  const firstGateIndex = stages.findIndex((stage) => stage.isGate)
  const visibleStages = firstGateIndex >= 0 ? stages.slice(0, firstGateIndex + 1) : stages

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ผลการจำลอง Workflow</DialogTitle>
          <DialogDescription>
            แสดงลำดับขั้นตอนอนุมัติที่ระบบคำนวณจากข้อมูลใบอนุญาตปัจจุบัน
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex flex-col items-center gap-2 py-6 text-sm text-slate-600">
            <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
            <span>กำลังประมวลผลเส้นทางอนุมัติ...</span>
          </div>
        ) : error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          </div>
        ) : result ? (
          <div className="space-y-4">
            <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <GitBranch className="h-4 w-4 text-indigo-500" />
                <span>Workflow Version</span>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                ID: <span className="font-mono text-slate-700">{result.workflowVersionId}</span>
              </p>
              {matchedRule ? (
                <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                  <div className="flex items-center gap-2 font-semibold">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Matched Rule: {matchedRule.name ?? matchedRule.rulesetId}</span>
                  </div>
                  <p className="text-xs text-emerald-700">
                    Priority {matchedRule.priority} • CAP Stage:{" "}
                    {matchedRule.capStageId ?? "ไม่ระบุ"}
                  </p>
                </div>
              ) : (
                <p className="mt-3 text-xs text-slate-500">ไม่พบ ruleset ที่ match</p>
              )}
            </section>

            <section className="space-y-3">
              <p className="text-sm font-semibold text-slate-900">ลำดับ Stage ที่ต้องอนุมัติ</p>
              <div className="relative">
                <div className="absolute left-1.5 top-2 h-full border-l border-dashed border-slate-300" />
                <div className="space-y-3">
                  {visibleStages.length === 0 ? (
                    <p className="text-xs text-slate-500">ยังไม่มีกำหนดขั้นตอน</p>
                  ) : (
                    visibleStages.map((stage) => <StageItem key={stage.stageId} stage={stage} />)
                  )}
                </div>
              </div>
            </section>

            {matchedRule?.conditions?.length ? (
              <>
                <Separator />
                <section className="space-y-2">
                  <p className="text-sm font-semibold text-slate-900">รายละเอียดเงื่อนไข</p>
                  <div className="space-y-2 text-xs text-slate-600">
                    {matchedRule.conditions.map((condition, index) => (
                      <div
                        key={`${condition.conditionId ?? condition.fieldCode ?? index}`}
                        className="rounded-md border border-slate-200 bg-slate-50 p-3"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-[11px]">
                            {condition.fieldCode ?? "FIELD"}
                          </span>
                          <Badge
                            variant={condition.passed ? "secondary" : "destructive"}
                            className="text-[10px]"
                          >
                            {condition.passed ? "PASS" : "FAIL"}
                          </Badge>
                        </div>
                        <p className="mt-1">
                          {condition.operator} {condition.expectedValue ?? "---"}
                        </p>
                        {condition.actualValue ? (
                          <p className="text-[11px] text-slate-500">
                            Actual: {condition.actualValue}
                          </p>
                        ) : null}
                        {condition.description ? (
                          <p className="text-[11px] text-slate-500">{condition.description}</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </section>
              </>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-slate-500">ยังไม่มีข้อมูลการจำลอง</p>
        )}
      </DialogContent>
    </Dialog>
  )
}
